import { useLocation } from 'react-router-dom';
import { MdOutlineAutoAwesome } from 'react-icons/md';
import { AgentSessionManager } from '@Components/AgentChat';
import { Drawer } from '@Components/Drawer';
import { Agent, useAgentEndpoint } from '@src/utils/agent-endpoints';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';

// The client-scoped Activity Feed route only (/:company/activity-feed, DEV-24983) -- not the
// older global /activity-feed, which has no single client to ask Oracle about.
const CLIENT_SCOPED_ACTIVITY_FEED_PATH = /^\/[^/]+\/activity-feed$/;

// The Oracle is an AgentCore runtime like the other agents (tinuiti-oracle-agent), reached through
// AgentSessionManager's default AG-UI transport. `company` is sent in the run's forwardedProps and
// pins the conversation to that client, so the agent never has to guess which client is meant.
// Gated to the client-scoped route so `company` is always the URL's own client, so there's no "no
// client selected" state to handle: the toggle simply doesn't render until useCompanyInfo().cid
// is populated.
export const ActivityFeedAgentDrawer = () => {
  const { pathname } = useLocation();
  const { cid: company } = useCompanyInfo();
  const enableLLM = useExperimentFlag('enableLLM');
  const agentEndpoint = useAgentEndpoint(Agent.ORACLE);

  if (!enableLLM || !company || !CLIENT_SCOPED_ACTIVITY_FEED_PATH.test(pathname)) return null;

  return (
    <Drawer
      title='Client Oracle Agent'
      toggleIcon={<MdOutlineAutoAwesome />}
      toggleAriaLabel={`Ask the Oracle agent about ${company}`}
    >
      {/* Keyed by client so switching clients remounts into a fresh session instead of leaving
          answers about the previous client in the same thread. */}
      <AgentSessionManager
        key={company}
        agent={Agent.ORACLE}
        agentEndpoint={agentEndpoint}
        company={company}
        placeholder='Ask about this client...'
        startFresh
      />
    </Drawer>
  );
};
